import type { ExperimentClient } from '@amplitude/experiment-js-client'

/**
 * Bootstraps and drives the Amplitude Experiment JS client (design.md DD10,
 * exposure-tracking spec "Client exposure SDK bootstrapped from edge
 * assignments"). It never fetches or evaluates flags itself: it is only
 * fed the variants already assigned at the edge
 * (`window.__EXPERIMENTS__`), filtered to Amplitude-routed keys
 * (`window.__EXPERIMENTS_PROVIDERS__`), and only ever calls `exposure(key)`.
 *
 * Consent/country gating (exposure-tracking spec, "Consent and country
 * gating") is enforced by the caller through `openExposureGate` /
 * `closeExposureGate`: this module never checks Didomi itself, so that the
 * same gate `nuxt-module-tracking`'s `didomiTracking` plugin already
 * computes for the main Amplitude Analytics SDK is the single source of
 * truth for both.
 *
 * Reads can happen before the gate opens (e.g. a component rendered before
 * consent is granted). Those are queued and flushed once the gate opens
 * (design.md DD10, "the fallback is to queue exposures in the module until
 * `didomiOnReady` and consent").
 */

let deploymentApiKey: string | null = null
let isGateOpen = false
let clientPromise: Promise<ExperimentClient> | null = null
const pendingKeys = new Set<string>()

function buildInitialVariants(): Record<string, { key: string }> {
  // eslint-disable-next-line no-underscore-dangle
  const experiments = window.__EXPERIMENTS__ ?? {}
  const providers: Record<string, string> =
    // eslint-disable-next-line no-underscore-dangle
    window.__EXPERIMENTS_PROVIDERS__ ?? {}

  const initialVariants: Record<string, { key: string }> = {}
  for (const [key, value] of Object.entries(experiments)) {
    if (providers[key] === 'amplitude') {
      initialVariants[key] = { key: value }
    }
  }

  return initialVariants
}

async function createClient(apiKey: string): Promise<ExperimentClient> {
  // Dynamic import so pages without any Amplitude-routed experiment never
  // load this SDK chunk (exposure-tracking spec target: no bundle weight
  // added to those pages).
  const { Experiment, Source } = await import('@amplitude/experiment-js-client')

  return Experiment.initializeWithAmplitudeAnalytics(apiKey, {
    serverZone: 'eu',
    initialVariants: buildInitialVariants(),
    source: Source.InitialVariants,
    fetchOnStart: false,
    pollOnStart: false,
    automaticExposureTracking: false,
  })
}

function flush() {
  if (!isGateOpen || !deploymentApiKey || pendingKeys.size === 0) return

  if (!clientPromise) {
    clientPromise = createClient(deploymentApiKey)
  }

  clientPromise
    .then((client) => {
      for (const key of pendingKeys) {
        client.exposure(key)
      }

      pendingKeys.clear()
    })
    .catch((error) => {
      // SDK failures must be non-fatal (exposure-tracking spec, "Exposure
      // tracking never blocks rendering"): catch, log, keep rendering.
      // eslint-disable-next-line no-console
      console.error('experiments.exposure_sdk_failed', { error })

      // Allow a later retry (e.g. a subsequent read) to try loading the SDK
      // again instead of being stuck on a permanently rejected promise.
      clientPromise = null
    })
}

/**
 * Sets the Amplitude deployment key used to initialize the Experiment SDK.
 * Reuses the same single project key as Amplitude Analytics (decisions.md
 * D8).
 */
export function configureExposureTracking(apiKey: string) {
  deploymentApiKey = apiKey
}

/**
 * Opens the gate: the same consent/country/tracker gating that already
 * guards the main Amplitude Analytics SDK. Flushes any key that was read
 * before the gate opened.
 */
export function openExposureGate() {
  isGateOpen = true
  flush()
}

/**
 * Closes the gate (e.g. consent withdrawn). Reads that happen while closed
 * are still queued, in case consent is granted again later in the same
 * page view.
 */
export function closeExposureGate() {
  isGateOpen = false
}

/**
 * Queues `key` for an exposure event, flushing immediately if the gate is
 * already open. Called once per key per page view by the
 * `experiments:exposure` Nuxt hook listener.
 */
export function queueExposure(key: string) {
  pendingKeys.add(key)
  flush()
}

/** Test-only: resets all module state between test cases. */
export function resetExposureTrackingStateForTests() {
  deploymentApiKey = null
  isGateOpen = false
  clientPromise = null
  pendingKeys.clear()
}
